/* eslint-disable react/require-default-props */
import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useState
} from 'react'
import { Form, Input } from 'antd'
import Krpano from 'utils/krpano'

import Spinner from 'components/atoms/Spinner'

import QuillEditorV2 from './ModalAlertSetting/QuillEditorV2'

type Props = {
  memo: string | undefined
  memoName?: string
  onMemoNameChange?: (name: string) => void
  onMemoContentChange?: (content: string) => void
  setDisableWhenMemoNotCompleted: Dispatch<SetStateAction<boolean>>
  setIsFetchingContent: Dispatch<SetStateAction<boolean>>
}

function MemoSetting({
  memo,
  memoName = '',
  onMemoNameChange,
  onMemoContentChange,
  setDisableWhenMemoNotCompleted,
  setIsFetchingContent
}: Props) {
  const [form] = Form.useForm()
  // Local state for Quill content — avoids Form.Item overriding value/onChange via cloneElement
  const [quillContent, setQuillContent] = useState<string>('')

  const validateForm = useCallback(
    (quillContent?: string) => {
      const nameValue = form.getFieldValue('memoName')
      const isMemoEmpty = Krpano.IsMemoEmpty(quillContent || '')

      const nameValid =
        nameValue?.trim().length >= 1 && nameValue?.trim().length <= 100
      const isFormValid = nameValid && !isMemoEmpty

      setDisableWhenMemoNotCompleted(!isFormValid)

      return isFormValid
      // eslint-disable-next-line react-hooks/exhaustive-deps
    },
    [form, setDisableWhenMemoNotCompleted]
  )

  /** Called by QuillEditorV2 whenever content changes; drives validation */
  const handleQuillChange = useCallback(
    (content: string) => {
      setQuillContent(content)
      onMemoContentChange?.(content)
      validateForm(content)
    },
    [validateForm, onMemoContentChange]
  )

  const handleFetchContent = useCallback(async () => {
    if (memo) {
      setIsFetchingContent(true)
      const textContent = await Krpano.FetchMemoContent(memo)

      // Push fetched HTML into local state so QuillEditorV2 receives it via `value`
      setQuillContent(textContent)
      onMemoContentChange?.(textContent)
      setIsFetchingContent(false)
      validateForm(textContent)
    }
  }, [memo, validateForm, setIsFetchingContent, onMemoContentChange])

  const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value
    form.setFieldsValue({ memoName: val })
    if (onMemoNameChange) {
      onMemoNameChange(val)
    }
    validateForm(quillContent)
  }

  useEffect(() => {
    handleFetchContent()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memo])

  useEffect(() => {
    if (memoName) {
      form.setFieldsValue({
        memoName
      })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memoName])

  return (
    <Spinner spinning={false}>
      <div className="pt-[10px] px-[158px] mb-2">
        <Form
          component={false}
          form={form}
          layout="vertical"
          requiredMark={false}
        >
          {memoName !== undefined && onMemoNameChange && (
            <div className="relative flex items-start my-1">
              <span className="whitespace-nowrap text-sm font-bold text-gray-700 mr-2 w-20 absolute -left-[10rem]  top-2 flex gap-2 items-center">
                メモ名
                <span className="text-red-500 text-xs whitespace-nowrap">
                  ※必須
                </span>
              </span>
              <Form.Item
                name="memoName"
                className="mb-0 flex-1 !w-1/2 !min-h-[72px]"
                rules={[
                  { required: true, message: 'メモ名を入力してください' },
                  {
                    min: 1,
                    max: 100,
                    message: '1〜100文字で入力してください'
                  }
                ]}
                initialValue={memoName}
              >
                <Input
                  placeholder="メモの名前を入力してください"
                  value={memoName}
                  onChange={handleNameChange}
                  className="w-full h-[38px]"
                />
              </Form.Item>
            </div>
          )}

          {/* No name prop — prevents Form.Item from overriding value/onChange via cloneElement */}
          <Form.Item>
            <div className="flex flex-col">
              <QuillEditorV2
                value={quillContent}
                onChange={handleQuillChange}
                height={350}
              />
            </div>
          </Form.Item>
        </Form>
      </div>
    </Spinner>
  )
}

export default MemoSetting
